import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabase';
import { MessageCircle, X, Send, Paperclip, ChevronLeft, Plus, Clock } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { getOrCreateSupportSessionId } from '../services/supportSession';

// Types
type Ticket = {
    id: string;
    category: string;
    status: string;
    created_at: string;
};

type Message = {
    id: string;
    sender_type: 'user' | 'admin';
    content: string;
    attachment_url: string | null;
    created_at: string;
};

export const SupportChat: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [session, setSession] = useState<Session | null>(null);
    const [localSessionId, setLocalSessionId] = useState('');

    // Views: 'list' | 'new' | 'chat'
    const [view, setView] = useState<'list' | 'new' | 'chat'>('list');

    // Data
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [activeTicket, setActiveTicket] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);

    // Form State
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [plan, setPlan] = useState('Free');
    const [category, setCategory] = useState('General Inquiry');

    // Chat State
    const [newMessage, setNewMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Initialize session ID for guests
        const sid = getOrCreateSupportSessionId();
        setLocalSessionId(sid);

        // Get Auth Session
        supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
            setSession(currentSession);
            if (currentSession?.user?.email) {
                setEmail(currentSession.user.email);
            }
        });

        supabase.auth.onAuthStateChange((_event, currentSession) => {
            setSession(currentSession);
        });
    }, []);

    useEffect(() => {
        if (isOpen && view === 'list' && (session?.user?.id || localSessionId)) {
            fetchTickets();
        }
    }, [isOpen, view, session, localSessionId]);

    useEffect(() => {
        if (view === 'chat' && activeTicket) {
            fetchMessages();

            const channel = supabase
                .channel(`support_${activeTicket}`)
                .on(
                    'postgres_changes',
                    { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `ticket_id=eq.${activeTicket}` },
                    (payload) => {
                        setMessages((prev) => [...prev, payload.new as Message]);
                        scrollToBottom();
                    }
                )
                .subscribe();

            return () => {
                supabase.removeChannel(channel);
            };
        }
    }, [view, activeTicket]);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    };

    const fetchTickets = async () => {
        setLoading(true);
        let query = supabase.from('support_tickets').select('id, category, status, created_at').order('created_at', { ascending: false });

        if (session?.user?.id) {
            query = query.eq('user_id', session.user.id);
        } else {
            query = query.eq('session_id', localSessionId);
        }

        const { data } = await query;
        if (data) {
            setTickets(data);
            if (data.length === 0) setView('new');
        }
        setLoading(false);
    };

    const fetchMessages = async () => {
        if (!activeTicket) return;
        const { data } = await supabase
            .from('support_messages')
            .select('*')
            .eq('ticket_id', activeTicket)
            .order('created_at', { ascending: true });

        if (data) {
            setMessages(data);
            scrollToBottom();
        }
    };

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        const ticketData = {
            user_id: session?.user?.id || null,
            session_id: session?.user?.id ? null : (localSessionId || getOrCreateSupportSessionId()),
            name,
            email,
            plan,
            category,
            status: 'open'
        };

        const { data, error } = await supabase
            .from('support_tickets')
            .insert(ticketData)
            .select()
            .single();

        if (!error && data) {
            setActiveTicket(data.id);
            setView('chat');
        }
        setLoading(false);
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim() && !isUploading) return;

        const messageData = {
            ticket_id: activeTicket,
            sender_type: 'user',
            content: newMessage.trim(),
        };

        setNewMessage('');
        await supabase.from('support_messages').insert(messageData);
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !activeTicket) return;

        setIsUploading(true);
        const fileExt = file.name.split('.').pop();
        const fileName = `${activeTicket}/${crypto.randomUUID()}.${fileExt}`;

        const { data: uploadData, error: uploadError } = await supabase.storage
            .from('support-attachments')
            .upload(fileName, file);

        if (uploadData) {
            const { data: { publicUrl } } = supabase.storage
                .from('support-attachments')
                .getPublicUrl(fileName);

            await supabase.from('support_messages').insert({
                ticket_id: activeTicket,
                sender_type: 'user',
                content: '📎 Sent an attachment',
                attachment_url: publicUrl
            });
        }
        setIsUploading(false);
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                aria-label="Open Live Chat"
                className="fixed bottom-6 right-6 p-4 bg-finance-accent text-black rounded-full shadow-[0_0_20px_rgba(240,192,96,0.3)] hover:scale-110 transition-all z-50 flex items-center justify-center group"
            >
                <div className="absolute bottom-full mb-3 px-3 py-1.5 bg-finance-card border border-gray-800 text-[#D4A537] text-[10px] font-black uppercase tracking-widest rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap shadow-2xl">
                    Live Chat
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
                <MessageCircle className="w-6 h-6 transition-transform duration-300 group-hover:-translate-y-1" />
            </button>
        );
    }

    return (
        <div className="fixed bottom-6 right-6 w-[350px] max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-100px)] bg-finance-card border border-gray-800 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden animate-in slide-in-from-bottom-5">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-finance-darker border-b border-gray-800">
                <div className="flex items-center gap-3">
                    {view === 'chat' && (
                        <button onClick={() => setView('list')} className="text-gray-400 hover:text-white">
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    )}
                    <div className="flex items-center gap-2 text-finance-accent font-bold">
                        <MessageCircle className="w-5 h-5" />
                        <span>ForexFund Support</span>
                    </div>
                </div>
                <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto bg-finance-dark/50 p-4 custom-scrollbar">
                {loading && view !== 'chat' ? (
                    <div className="flex items-center justify-center h-full text-gray-500">Loading...</div>
                ) : view === 'list' ? (
                    <div className="space-y-4">
                        <button
                            onClick={() => setView('new')}
                            className="w-full flex items-center justify-center gap-2 p-3 bg-finance-accent text-black font-bold rounded-xl hover:bg-yellow-500 transition-colors"
                        >
                            <Plus className="w-5 h-5" /> New Conversation
                        </button>
                        <div className="space-y-3">
                            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-6">Your Tickets</h4>
                            {tickets.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No past conversations.</p>
                            ) : (
                                tickets.map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => { setActiveTicket(t.id); setView('chat'); }}
                                        className="w-full flex items-center justify-between p-4 bg-finance-darker border border-gray-800 rounded-xl hover:border-gray-700 transition-all text-left group"
                                    >
                                        <div>
                                            <p className="text-sm font-semibold text-white group-hover:text-finance-accent transition-colors">{t.category}</p>
                                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> {formatDate(t.created_at)}</p>
                                        </div>
                                        <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded-full ${t.status === 'open' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-800 text-gray-400'}`}>
                                            {t.status}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                ) : view === 'new' ? (
                    <form onSubmit={handleCreateTicket} className="space-y-4">
                        <p className="text-sm text-gray-400 mb-4">Enter your details to start a conversation.</p>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 ml-1">Name *</label>
                            <input required type="text" value={name} onChange={e => setName(e.target.value)} className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl px-4 py-3 focus:border-finance-accent outline-none text-sm" placeholder="Your name" />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 ml-1">Email *</label>
                            <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl px-4 py-3 focus:border-finance-accent outline-none text-sm" placeholder="your@email.com" />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 ml-1">Current Plan</label>
                            <select value={plan} onChange={e => setPlan(e.target.value)} className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl px-4 py-3 focus:border-finance-accent outline-none text-sm appearance-none">
                                <option value="Free">Free</option>
                                <option value="Pro">Pro ($29/mo)</option>
                                <option value="Premium">Premium ($49/mo)</option>
                                <option value="Elite">Elite ($99/mo)</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1 ml-1">How can we help?</label>
                            <select value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-[#0b0f19] border border-gray-800 text-white rounded-xl px-4 py-3 focus:border-finance-accent outline-none text-sm appearance-none">
                                <option value="General Inquiry">General Inquiry</option>
                                <option value="Payment Issue">Payment Issue</option>
                                <option value="Signal Problem">Signal Problem</option>
                                <option value="Bug Report">Bug Report</option>
                            </select>
                        </div>

                        <button type="submit" disabled={loading} className="w-full py-3 bg-finance-accent text-black font-bold rounded-xl flex items-center justify-center gap-2 mt-2 hover:bg-yellow-500 transition-colors disabled:opacity-50">
                            <Send className="w-4 h-4" /> Start Chat
                        </button>

                        {tickets.length > 0 && (
                            <button type="button" onClick={() => setView('list')} className="w-full py-3 text-gray-400 text-sm font-bold hover:text-white transition-colors">
                                Cancel
                            </button>
                        )}
                    </form>
                ) : (
                    <div className="flex flex-col space-y-4">
                        {messages.length === 0 ? (
                            <p className="text-center text-sm text-gray-500 my-auto py-10">An agent will be with you shortly.</p>
                        ) : (
                            messages.map(msg => (
                                <div key={msg.id} className={`flex flex-col max-w-[85%] ${msg.sender_type === 'user' ? 'self-end' : 'self-start'}`}>
                                    <div className={`p-3 rounded-2xl ${msg.sender_type === 'user' ? 'bg-blue-600 rounded-br-none text-white' : 'bg-gray-800 rounded-bl-none text-gray-200'}`}>
                                        {msg.content && <p className="text-sm">{msg.content}</p>}
                                        {msg.attachment_url && (
                                            <a href={msg.attachment_url} target="_blank" rel="noopener noreferrer" className="block mt-2">
                                                <img src={msg.attachment_url} alt="Attachment" className="max-w-full rounded-lg border border-white/20" />
                                            </a>
                                        )}
                                    </div>
                                    <span className={`text-[10px] text-gray-500 mt-1 ${msg.sender_type === 'user' ? 'text-right' : 'text-left'}`}>
                                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>

            {/* Chat Input */}
            {view === 'chat' && (
                <div className="p-3 bg-finance-darker border-t border-gray-800">
                    <form onSubmit={handleSendMessage} className="flex items-end gap-2">
                        <label className="p-2 text-gray-400 hover:text-finance-accent cursor-pointer transition-colors shrink-0">
                            <Paperclip className="w-5 h-5" />
                            <input type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.doc,.docx" disabled={isUploading} />
                        </label>
                        <div className="flex-1 bg-[#0b0f19] border border-gray-800 rounded-xl flex items-center pr-2 focus-within:border-gray-600 transition-colors">
                            <input
                                type="text"
                                value={newMessage}
                                onChange={e => setNewMessage(e.target.value)}
                                placeholder="Type a message..."
                                className="w-full bg-transparent text-white px-3 py-2.5 outline-none text-sm placeholder:text-gray-600"
                            />
                            <button type="submit" disabled={!newMessage.trim()} className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600 transition-colors">
                                <Send className="w-4 h-4 ml-0.5" />
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};
